// Who's here (W9b): coloured dots on contents rows and corkboard cards for the members in them, a line in Write's
// footer ("Anna is in this scene", "Anna is writing here"), and a soft claim — a quiet note when someone else is in
// the scene this writer's caret is in. Nothing is locked: the note says so, and either can keep going.
import { useEffect, useState } from 'react';
import { usePresence, type PresencePerson } from '../store/presence.ts';
import '../styles/presence.css';

/** "Anna", "Anna and Bea", "Anna, Bea and Cam". */
export function namesOf(people: Pick<PresencePerson, 'name'>[]): string {
  const names = people.map((p) => p.name);
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Dots for the members in any of `fragmentIds` (a row's or card's fragment, and a folder's under it). */
export function PresenceDots({ fragmentIds }: { fragmentIds: readonly string[] }) {
  const { others } = usePresence();
  if (!others.length) return null;
  const here = others.filter((p) => p.fragmentId && fragmentIds.includes(p.fragmentId));
  if (!here.length) return null;
  const label = `${namesOf(here)} ${here.length === 1 ? 'is' : 'are'} here`;
  return (
    <span className="presence-dots" role="img" aria-label={label} title={label} data-presence={here.map((p) => p.name).join(', ')}>
      {here.map((p) => (
        <span key={p.account} className={`presence-dot${p.typing ? ' typing' : ''}`} ref={(el) => el?.style.setProperty('--member', p.color)} data-member={p.account} />
      ))}
    </span>
  );
}

/** Write's footer: who else is in what's shown, and whether they're writing. */
export function PresenceLine({ ids, titleOf }: { ids: readonly string[]; titleOf(fragmentId: string): string }) {
  const { others } = usePresence();
  const here = others.filter((p) => p.fragmentId && ids.includes(p.fragmentId));
  if (!here.length) return null;
  const writing = here.filter((p) => p.typing);
  const reading = here.filter((p) => !p.typing);
  const where = (people: PresencePerson[]) => {
    if (ids.length === 1) return null;
    const scenes = [...new Set(people.map((p) => p.fragmentId!))];
    return scenes.length === 1 ? `“${titleOf(scenes[0]) || 'Untitled'}”` : null;
  };
  const parts: string[] = [];
  if (writing.length) parts.push(`${namesOf(writing)} ${writing.length === 1 ? 'is' : 'are'} writing ${where(writing) ? `in ${where(writing)}` : 'here'}`);
  if (reading.length) parts.push(`${namesOf(reading)} ${reading.length === 1 ? 'is' : 'are'} in ${where(reading) ?? 'this scene'}`);
  return (
    <span className="foot-presence" data-presence-line={here.map((p) => p.account).join(' ')} data-writing={writing.length > 0 || undefined}>
      <span className="presence-dots" aria-hidden="true">
        {here.map((p) => (
          <span key={p.account} className={`presence-dot${p.typing ? ' typing' : ''}`} ref={(el) => el?.style.setProperty('--member', p.color)} />
        ))}
      </span>
      {parts.join(' · ')}
    </span>
  );
}

/**
 * The soft claim: when someone else is in the scene this writer's caret is in, a quiet note saying so, once per arrival
 * (dismissed, it stays away until they leave and come back). Both writers see it.
 */
export function PresenceClaim() {
  const { others, mine } = usePresence();
  const here = mine ? others.filter((p) => p.fragmentId === mine) : [];
  const key = mine ? `${mine}:${here.map((p) => p.account).sort().join(',')}` : '';
  const [dismissed, setDismissed] = useState<string | null>(null);
  useEffect(() => {
    if (!here.length) setDismissed(null);
  }, [here.length]);
  if (!here.length || dismissed === key) return null;
  return (
    <div className="presence-claim" role="status" data-presence-claim={here.map((p) => p.account).join(' ')}>
      <span className="presence-dots" aria-hidden="true">
        {here.map((p) => (
          <span key={p.account} className="presence-dot" ref={(el) => el?.style.setProperty('--member', p.color)} />
        ))}
      </span>
      <span>
        {namesOf(here)} {here.length === 1 ? 'is' : 'are'} in this scene too. Nothing is locked: you can both keep writing.
      </span>
      <button type="button" className="quiet-button" onClick={() => setDismissed(key)} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}
